import {useCallback, useEffect, useRef, useState} from "react";
import {Observer} from "mobx-react";
import type {DocumentModel} from "../../../data/models/DocumentModel";
import type {IModel} from "../../../data/models/Model";
import {XyiconFeature} from "../../../generated/api/base";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {Markup} from "../../../data/models/Markup";
import {useAppStore} from "../../../StateManager";
import type {AppState} from "../../../data/state/AppState";
import {ItemsNumberWhenExpanded, ScopeWideSearchV5} from "./ScopeWideSearchV5";
import {getScopeWideSearchFeatures} from "./OrganizationWideSearchUtils";

const getFilteredList = (
	appState: AppState,
	portfolioId: string,
	queryString: string,
	lastResult: {portfolioId: string; queryString: string; result: IModel[]},
): IModel[] => {
	if (queryString.length !== 0) {
		if (lastResult.portfolioId === portfolioId && lastResult.queryString === queryString) {
			// Don't search again if query or portfolio didn't change
			return lastResult.result;
		}

		const result: IModel[] = [];

		const featuresToSearch = [XyiconFeature.Xyicon, XyiconFeature.XyiconCatalog, XyiconFeature.Boundary, XyiconFeature.Space, XyiconFeature.Markup];

		for (const feature of featuresToSearch) {
			const array = appState.actions.getList(feature);
			const matching = appState.actions.searchModelsCached(array, queryString, feature, ItemsNumberWhenExpanded);

			result.push(...matching);
		}

		appState.actions.getList<Markup>(XyiconFeature.Markup).forEach((markup: Markup) => {
			if (
				!result.includes(markup) &&
				(StringUtils.containsIgnoreCase(markup.textContent, queryString) ||
					StringUtils.containsIgnoreCase(markup.typeNameAsText, queryString) ||
					StringUtils.containsIgnoreCase(markup.spaceName, queryString))
			) {
				result.push(markup);
			}
		});

		const portfolioDocuments = appState.actions
			.getList<DocumentModel>(XyiconFeature.PortfolioDocument)
			.filter((d: DocumentModel) => d.parentElementMaybe);
		const orgDocuments = appState.actions
			.getList<DocumentModel>(XyiconFeature.OrganizationDocument)
			.filter(
				(d: DocumentModel) =>
					(d.documentMap[0].feature === XyiconFeature.XyiconCatalog ||
						(d.documentMap[0].feature === XyiconFeature.Portfolio && d.data.portfolioID === appState.activePortfolioId)) &&
					d.parentElementMaybe,
			);

		const allDocuments = [...portfolioDocuments, ...orgDocuments];

		allDocuments.forEach((doc: DocumentModel) => {
			if (StringUtils.containsIgnoreCase(doc.fileName, queryString)) {
				result.push(doc);
			}
		});

		lastResult.queryString = queryString;
		lastResult.result = result;
		lastResult.portfolioId = portfolioId;

		return result;
	} else {
		return [];
	}
};

interface IPortfolioWideSearchProps {
	readonly queryString: string;
	readonly portfolioId: string;
	readonly closeScopeWideSearch: () => void;
}

export const PortfolioWideSearchV5 = (props: IPortfolioWideSearchProps) => {
	const {queryString, portfolioId, closeScopeWideSearch} = props;
	const appState = useAppStore((state) => state.appState);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const _lastResult = useRef<{
		queryString: string;
		portfolioId: string;
		result: IModel[];
	}>({
		queryString: "",
		portfolioId,
		result: [],
	});

	const featureService = appState.app.transport.services.feature;

	const loadFeatureItems = useCallback(() => {
		const featuresToLoad = [...getScopeWideSearchFeatures("Portfolio"), XyiconFeature.User];

		Promise.all(featuresToLoad.map((feature) => featureService.refreshList(feature))).then(() => {
			setIsLoading(false);
		});
	}, [featureService]);

	useEffect(() => {
		loadFeatureItems();
	}, [loadFeatureItems]);

	return (
		<Observer>
			{() => {
				const filteredList = isLoading ? [] : getFilteredList(appState, portfolioId, queryString, _lastResult.current);

				return (
					<ScopeWideSearchV5
						scopeWideSearchType="Portfolio"
						queryString={queryString}
						results={filteredList}
						isLoading={isLoading}
						closeScopeWideSearch={closeScopeWideSearch}
					/>
				);
			}}
		</Observer>
	);
};
